import type { CSSProperties } from "react";
import { keycapCatalog, keycapIconComponents, type KeycapId } from "./keycapCatalog";
import "./Keycap.css";

export type KeycapSize = "sm" | "md" | "lg" | "xl";

/**
 * `active` — lit and pressed, as if currently in use.
 * `unavailable` — dimmed and unlit: not yet built, or not yet permitted.
 * Always paired with a text status nearby (e.g. `StatusBadge`) — colour
 * alone never carries this meaning.
 */
export type KeycapState = "default" | "active" | "unavailable";

export interface KeycapProps {
  id: KeycapId;
  size?: KeycapSize;
  /** Set beneath the key. The cap face itself carries only the legend. */
  label?: string;
  /** Decorative by default — the surrounding copy names the concept. */
  alt?: string;
  state?: KeycapState;
  className?: string;
}

/**
 * One Devory key: a sculpted keycap with a coloured legend and a resting
 * RGB-style underglow, in the spirit of a physical macropad keyset rather
 * than a flat tile. See the 2026-07-17 keycap art-direction pass.
 */
export function Keycap({ id, size = "md", label, alt, state = "default", className }: KeycapProps) {
  const { icon, accent } = keycapCatalog[id];
  const Icon = keycapIconComponents[icon];
  const decorative = !alt;

  return (
    <span className={["keycap-wrap", className].filter(Boolean).join(" ")}>
      <span
        className={["keycap", `keycap-${size}`, state !== "default" ? `keycap-${state}` : ""]
          .filter(Boolean)
          .join(" ")}
        style={{ "--keycap-accent": accent } as CSSProperties}
        role={decorative ? undefined : "img"}
        aria-hidden={decorative ? true : undefined}
        aria-label={alt}
      >
        <img
          className="keycap-depth keycap-depth-far"
          src="/devory/tile-base.webp"
          srcSet="/devory/tile-base.webp 1x, /devory/tile-base@2x.webp 2x"
          alt=""
          aria-hidden="true"
          draggable={false}
          decoding="async"
        />
        <img
          className="keycap-depth keycap-depth-near"
          src="/devory/tile-base.webp"
          srcSet="/devory/tile-base.webp 1x, /devory/tile-base@2x.webp 2x"
          alt=""
          aria-hidden="true"
          draggable={false}
          decoding="async"
        />
        <img
          className="keycap-body"
          src="/devory/tile-base.webp"
          srcSet="/devory/tile-base.webp 1x, /devory/tile-base@2x.webp 2x"
          alt=""
          aria-hidden="true"
          draggable={false}
          decoding="async"
        />
        <span className="keycap-face">
          <Icon className="keycap-icon" />
        </span>
      </span>
      {label ? <span className="keycap-label">{label}</span> : null}
    </span>
  );
}
